import { Component, ElementRef, ViewChild } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { nav_path } from '../../../app-routing.module';
import { map, startWith, switchMap } from 'rxjs/operators';
import { FirestoreService } from '../../../shared/services/firestore.service';
import { where } from '@angular/fire/firestore';
import { StoryService } from '../../../shared/services/story.service';
import { readStory, StoryStatus, StoryType } from '../../../shared/interfaces/story';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { SeoService } from '../../../core/services/seo.service';
import { MatChipInputEvent } from "@angular/material/chips";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { FormControl } from "@angular/forms";
import { COMMA, ENTER } from "@angular/cdk/keycodes";

@Component({
  selector: 'anon-stories',
  templateUrl: './stories.component.html',
  styleUrls: ['./stories.component.scss']
})
export class StoriesComponent {
  public stories$?: Observable<readStory[]>;
  public storyTags: string[] = [];
  public filterByTags: string[] = [];
  public error?: Error;
  public readonly nav_path = nav_path;
  private title = "User stories page";
  public storyTypes: StoryType[] = [];
  public filterType: StoryType | 'all' = 'all';
  public storyStatuses: StoryStatus[] = [];
  public filterStatus: StoryStatus | 'all' = 'all';
  public tagControl = new FormControl<string>('');
  @ViewChild('tagInput') tagInput?: ElementRef<HTMLInputElement>;
  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  public allStoryTags: string[] = [];
  public filteredTags?: Observable<string[]>;

  constructor(
    private db: FirestoreService,
    private storyService: StoryService,
    private auth: AuthService,
    private state: TransferState,
    private seo: SeoService
  ) {
    seo.generateTags({
      title: this.title,
      description: `Anonymous Systems ${this.title}`,
      route: nav_path.privateStories
    });

    const key = makeStateKey<readStory[]>('STORIES');
    const existing = state.get(key,undefined);
    this.stories$ = of(existing).pipe(
      switchMap(() => this.db.colQuery$(
        `stories`,
        { idField: 'id' },
        where(`roles.${auth.user?.uid}`, '==', 'owner')
      )),
      map(stories => this.storyService.sort(stories as readStory[], 'desc')),
      tap((stories: any) => {
        state.set(key, stories);

        stories.forEach((story: readStory) => {
          story.tags?.forEach((tag) => {
            if (!this.storyTags.includes(tag)) this.storyTags.push(tag)
          })
        });
        this.storyTags = this.storyService.getStoriesTags(stories);
        this.allStoryTags = this.storyService.getStoriesTags(stories);
        this.storyTypes = this.storyService.getStoriesTypes(stories);
        this.storyStatuses = this.storyService.getStoriesStatuses(stories);

        return stories;
      }),
      existing ? startWith(existing) : tap()
    );

    this.filteredTags = this.tagControl.valueChanges.pipe(
      startWith(null),
      map((tag: string | null) => (tag ? this._filter(tag) : this.allStoryTags.slice().filter(tag => !this.filterByTags.includes(tag))))
    );
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    /* Only add existing tags */
    if (!this.allStoryTags.includes(value)) return;

    /* Add tag */
    if (value) this.filterByTags.push(value);

    /* Clear the input value */
    event.chipInput.clear();

    this.tagControl.setValue(null);
  }
  remove(tag: string): void {
    const index = this.filterByTags.indexOf(tag);

    if (index >= 0) this.filterByTags.splice(index, 1);
  }
  selected(event: MatAutocompleteSelectedEvent): void {
    this.filterByTags.push(event.option.viewValue);
    (<ElementRef<HTMLInputElement>>this.tagInput).nativeElement.value = '';
    this.tagControl.setValue(null);
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.allStoryTags.filter(tag => !this.filterByTags.includes(tag))
      .filter(tag => tag.toLowerCase().includes(filterValue));
  }

  public _filterByTags(stories: readStory[], tags?: string[]): readStory[] {
    if (!tags || !tags.length) return stories;

    return stories.filter(story => {
      return story.tags?.some(tag => {
        return Array.isArray(tags) ? tags.includes(tag) : tag == tags;
      });
    });
  }
}
